import { CRUDInputElementConstants, ECRUDFormLayoutType } from "@kuika/kuika-cl-model";
import type { ICRUDFormDiMetadata, ICRUDFormField, IFormSection } from "@kuika/kuika-cl-model";
import _ from "lodash";
import {
  describeElementTypeOptions,
  isElementTypeValidForFieldType,
  resolveElementTypeName
} from "./form-element-type-options";

/**
 * KOA-17362 §7.6 Eksen 1 — DSL-authored field configuration for the MANAGED task
 * screen of a workflow user task.
 *
 * A user task's screen is generated by `workflowAssignScreenToUserTaskAction`
 * (ASSIGN_SCREEN) from the default `ICRUDFormDiMetadata` the backend derives from
 * the workflow's business-object parameters: ONE untitled section, every BO param
 * enabled and EDITABLE, type-default widget. That is the wrong shape for an
 * approval task — the approver should REVIEW the request read-only and only edit
 * what the task itself decides.
 *
 * The whole capability already exists end-to-end: the human form wizard writes the
 * exact same metadata knobs, `FormMetadataService.SyncFormMetadataWithWorkflow`
 * PRESERVES them (it only adds/removes/re-links params), and
 * `FormBuilderService.GetFormDiCustom` honors each one — `isEnabled` (field is not
 * rendered at all), `isReadonlyField` (a view column instead of an input),
 * `isRequired` (validation injection), and `name` (the label DI's text). It was
 * simply not reachable from the DSL. This module is that reach: a PURE merge of
 * DSL field config onto the default metadata, applying the SAME mutual-exclusion
 * rules the wizard enforces in its UI.
 *
 * Pure by design (no `sd`, no service, no window) so the rules are unit-testable
 * and identical on every platform and culture.
 */

/** One field's configuration, addressed by its workflow business-object param name. */
export interface WorkflowFormFieldOverride {
  /** The BO parameter name (as declared by `PARAM BO`). Matched culture-invariantly. */
  readonly param: string;
  /** true → the field is not rendered at all (`isEnabled: false`). */
  readonly hidden?: boolean;
  /** true → rendered as a read-only view column instead of an input. */
  readonly isReadonly?: boolean;
  /** true → required validation. Ignored (with a warning) when the field is readonly. */
  readonly isRequired?: boolean;
  /** Visible label. Rewrites the field's `name`, which is what the label DI renders. */
  readonly label?: string;
  /** 1-based position inside its section. Unspecified fields keep their relative order. */
  readonly order?: number;
  /**
   * Input WIDGET (`elementType`). Any spelling `resolveElementTypeName` accepts —
   * canonical ("Picker Date Time"), space-free ("pickerdatetime") or the wizard's UI
   * label ("Switch", "NumberInput", "SelectBox"). Must be one of the widgets the
   * field's data type offers, or the whole override set is refused.
   */
  readonly element?: string;
  /** Decimal places (`decimalScale`). Numeric fields only; ignored with a warning otherwise. */
  readonly scale?: number;
  /**
   * Title of the SECTION this field belongs to. Matched culture-invariantly against the
   * existing sections; a title no section carries is created (declaring it with a
   * section override first is preferred, since that also sets its layout).
   */
  readonly section?: string;
}

/** Column counts a form section can lay its fields out in (`ECRUDFormLayoutType`). */
export interface WorkflowFormLayoutOverride {
  /** Desktop columns: 1, 2 or 4. */
  readonly columns?: number;
  /** Tablet columns: 1, 2 or 4. */
  readonly tablet?: number;
  /** Mobile columns: 1, 2 or 4. */
  readonly mobile?: number;
}

/** A titled section of the form, created when the title does not exist yet. */
export interface WorkflowFormSectionOverride extends WorkflowFormLayoutOverride {
  readonly title: string;
}

export interface WorkflowFormOverrides {
  readonly fields: readonly WorkflowFormFieldOverride[];
  /** Titled sections to ensure exist, in declaration order. */
  readonly sections?: readonly WorkflowFormSectionOverride[];
  /** Layout of the form's DEFAULT (first) section. */
  readonly form?: WorkflowFormLayoutOverride;
}

export interface WorkflowFormOverrideApplication {
  /** The metadata to send to `UpdateWorkflowScreens`. The input is never mutated. */
  readonly metadata: ICRUDFormDiMetadata;
  /** Param names that matched a field and were applied, in override order. */
  readonly appliedParams: readonly string[];
  /** Param names with no matching field — the caller surfaces a fixable error. */
  readonly unknownParams: readonly string[];
  /** Non-fatal deviations (a knob combination the wizard forbids, so it was dropped). */
  readonly warnings: readonly string[];
  /** Set when the override set is refused wholesale; `metadata` is then the untouched input. */
  readonly error?: string;
}

/**
 * Fold a workflow parameter name to a comparable token so DSL-authored names line
 * up with the real BO parameter names regardless of culture, spacing, or case.
 *
 * Same convention as `foldForScreenNameMatch` (step name ↔ generated screen name)
 * but a DIFFERENT domain — this one matches a DSL `"<boParam>"` reference against
 * `linkedWorkflowBoParameterName`. Kept local so this module stays pure and free
 * of the designer-action graph. The explicit Turkish letter lines cover the pairs
 * NFKD leaves intact (ı/İ/ğ/ş): `char.toUpperCase()`-style locale traps must never
 * decide whether a field is found.
 */
export function foldParamNameForMatch(value: string): string {
  return (value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i")
    .replace(/ğ/g, "g")
    .replace(/Ğ/g, "g")
    .replace(/ş/g, "s")
    .replace(/Ş/g, "s")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Field types that carry a decimal scale (the renderer only reads it for these). */
const DECIMAL_SCALE_TYPES: readonly string[] = ["Decimal", "Double", "Int16", "Int32", "Int64"];

/** The element types whose input has no "empty" state, so required is meaningless. */
const REQUIRED_INCOMPATIBLE_ELEMENTS: readonly string[] = [
  CRUDInputElementConstants.Checkbox,
  CRUDInputElementConstants.ToggleSwitch
];

/** A field's identity for matching: the BO link first, its (renameable) label last. */
function fieldMatchKey(field: ICRUDFormField): string {
  const linked: string = field.linkedWorkflowBoParameterName ?? "";
  return foldParamNameForMatch(linked.length > 0 ? linked : field.name);
}

interface FieldLocation {
  readonly sectionIndex: number;
  readonly itemIndex: number;
}

function indexFieldsByParam(metadata: ICRUDFormDiMetadata): Map<string, FieldLocation> {
  const index: Map<string, FieldLocation> = new Map<string, FieldLocation>();
  metadata.sections.forEach((section: IFormSection, sectionIndex: number): void => {
    section.items.forEach((item: ICRUDFormField, itemIndex: number): void => {
      const key: string = fieldMatchKey(item);
      // First declaration wins: a duplicate spelling must not silently retarget.
      if (key.length > 0 && !index.has(key)) index.set(key, { sectionIndex, itemIndex });
    });
  });
  addLabelAliases(metadata, index);
  return index;
}

/**
 * Also address each field by its VISIBLE LABEL, when that is unambiguous.
 *
 * `fieldMatchKey` uses the BO parameter name whenever the field is linked, and
 * falls back to the label only for an unlinked field — so on a real managed task
 * screen (every field BO-linked) the label was NOT a usable reference. That is a
 * problem for the caller the DSL redirect actually has: an author looking at the
 * canvas sees "Adet" / "Birim fiyat" / "Gerekçe" (the label DIs), NOT the BO
 * parameter spelling, which lives in workflow metadata the screen does not show.
 * The first live smoke (chat_1785187254829_9ssyua) ended with the planner trying
 * to DISCOVER those names through a `LIST` that cannot run from a screen.
 *
 * So a label is accepted as an alias, under two rules that keep it safe:
 *  - a BO parameter key ALWAYS wins (aliases never overwrite an existing key), so
 *    the canonical spelling stays authoritative and behaviour is unchanged for
 *    scripts that use it;
 *  - a label shared by two fields registers NO alias at all, because guessing
 *    which of them the author meant is worse than reporting an unknown name.
 */
function addLabelAliases(metadata: ICRUDFormDiMetadata, index: Map<string, FieldLocation>): void {
  const seen: Map<string, FieldLocation | null> = new Map<string, FieldLocation | null>();
  metadata.sections.forEach((section: IFormSection, sectionIndex: number): void => {
    section.items.forEach((item: ICRUDFormField, itemIndex: number): void => {
      const alias: string = foldParamNameForMatch(item.name ?? "");
      if (alias.length === 0) return;
      // null marks an ambiguous alias — two fields carry this label.
      seen.set(alias, seen.has(alias) ? null : { sectionIndex, itemIndex });
    });
  });
  for (const [alias, location] of seen) {
    if (location === null) continue;
    if (index.has(alias)) continue;
    index.set(alias, location);
  }
}

/**
 * Every spelling an author may use to reference a field — the BO parameter name
 * and, when it differs and is unambiguous, the visible label. Used to make the
 * unknown-parameter error state what would actually have worked.
 */
export function describeFieldReferenceNames(metadata: ICRUDFormDiMetadata): string[] {
  const names: string[] = [];
  const seenLabels: Map<string, number> = new Map<string, number>();
  for (const section of metadata.sections) {
    for (const item of section.items) {
      const label: string = foldParamNameForMatch(item.name ?? "");
      if (label.length > 0) seenLabels.set(label, (seenLabels.get(label) ?? 0) + 1);
    }
  }
  for (const section of metadata.sections) {
    for (const item of section.items) {
      const linked: string = item.linkedWorkflowBoParameterName ?? "";
      const label: string = item.name ?? "";
      const canonical: string = linked.length > 0 ? linked : label;
      if (canonical.length === 0) continue;
      const labelKey: string = foldParamNameForMatch(label);
      const showsLabel: boolean =
        label.length > 0 && foldParamNameForMatch(canonical) !== labelKey && (seenLabels.get(labelKey) ?? 0) === 1;
      names.push(showsLabel ? `${canonical} (label: "${label}")` : canonical);
    }
  }
  return names;
}

function countEnabledFields(metadata: ICRUDFormDiMetadata): number {
  return metadata.sections.reduce(
    (total: number, section: IFormSection): number =>
      total + section.items.filter((item: ICRUDFormField): boolean => item.isEnabled).length,
    0
  );
}

/**
 * Column count → layout enum. Only 1, 2 and 4 exist (`ECRUDFormLayoutType`); any other
 * count is an authoring mistake we refuse rather than round to something arbitrary.
 */
function resolveLayoutType(columns: number): ECRUDFormLayoutType | undefined {
  if (columns === 1) return ECRUDFormLayoutType.SingleColumn;
  if (columns === 2) return ECRUDFormLayoutType.TwoColumn;
  if (columns === 4) return ECRUDFormLayoutType.FourColumn;
  return undefined;
}

/** Culture-invariant section-title key, so one title never yields two sections. */
function foldSectionTitle(title: string): string {
  return foldParamNameForMatch(title);
}

/**
 * Apply a layout override to a section in place. Unspecified breakpoints keep the
 * layout the section already had (the backend's default is 2/2/1).
 */
function applySectionLayout(section: IFormSection, layout: WorkflowFormLayoutOverride): void {
  const desktop: ECRUDFormLayoutType | undefined =
    layout.columns !== undefined ? resolveLayoutType(layout.columns) : undefined;
  const tablet: ECRUDFormLayoutType | undefined =
    layout.tablet !== undefined ? resolveLayoutType(layout.tablet) : undefined;
  const mobile: ECRUDFormLayoutType | undefined =
    layout.mobile !== undefined ? resolveLayoutType(layout.mobile) : undefined;
  if (desktop !== undefined) section.responsiveLayoutSizes.desktop = desktop;
  if (tablet !== undefined) section.responsiveLayoutSizes.tablet = tablet;
  if (mobile !== undefined) section.responsiveLayoutSizes.mobile = mobile;
}

/** Every column count in a layout override that is not 1, 2 or 4. */
function invalidColumnCounts(layout: WorkflowFormLayoutOverride): number[] {
  return [layout.columns, layout.tablet, layout.mobile].filter(
    (count: number | undefined): count is number => count !== undefined && resolveLayoutType(count) === undefined
  );
}

/**
 * A new titled section. `id` is DERIVED from the title (not random) so re-applying the
 * same config produces byte-identical metadata; `predefinedIds` is left out because the
 * backend fills every kuika id it needs (`FormMetadataService.FillPredefinedIds`). The
 * default breakpoints mirror the wizard's own "add section" (2 / 2 / 1).
 */
function createSection(title: string): IFormSection {
  return {
    id: `screen-section-${foldSectionTitle(title)}`,
    title,
    items: [],
    responsiveLayoutSizes: {
      desktop: ECRUDFormLayoutType.TwoColumn,
      tablet: ECRUDFormLayoutType.TwoColumn,
      mobile: ECRUDFormLayoutType.SingleColumn
    }
  };
}

/** Index of the section carrying `title` (culture-invariant), or -1. */
function findSectionIndexByTitle(metadata: ICRUDFormDiMetadata, title: string): number {
  const wanted: string = foldSectionTitle(title);
  return metadata.sections.findIndex(
    (section: IFormSection): boolean => foldSectionTitle(section.title ?? "") === wanted
  );
}

/**
 * Apply DSL field configuration to a managed task form's metadata.
 *
 * Rules mirrored from the wizard (`form-section.tsx`), so DSL-authored and
 * hand-authored forms can never diverge:
 *   • readonly ⇒ required is forced OFF (the wizard disables the checkbox).
 *   • required on a Checkbox / ToggleSwitch is dropped (same disable rule).
 *   • `label` rewrites `name` only; the BO link that identifies the field is kept,
 *     so a re-applied override still finds it (idempotent).
 *   • `order` is a stable sort inside the field's own section.
 *
 * Refused wholesale (nothing applied) when the result would enable NO field: the
 * backend does NOT guard this for a workflow form — `ValidateMetadataOutput`
 * accepts a fieldless WorkflowBo metadata — so an all-hidden set would silently
 * ship a blank approval screen. Better a fixable error than an empty screen.
 */
export function applyWorkflowFormOverrides(
  metadata: ICRUDFormDiMetadata,
  overrides: WorkflowFormOverrides
): WorkflowFormOverrideApplication {
  const next: ICRUDFormDiMetadata = _.cloneDeep(metadata);
  const sectionOverrides: readonly WorkflowFormSectionOverride[] = overrides.sections ?? [];
  const hasWork: boolean = overrides.fields.length > 0 || sectionOverrides.length > 0 || overrides.form !== undefined;
  if (!hasWork) {
    return { metadata: next, appliedParams: [], unknownParams: [], warnings: [] };
  }

  // Layout counts are validated first: an unsupported column count is an authoring
  // mistake, and applying half a layout would leave the form in a shape the author
  // never asked for.
  for (const layout of [...(overrides.form ? [overrides.form] : []), ...sectionOverrides]) {
    const invalid: number[] = invalidColumnCounts(layout);
    if (invalid.length > 0) {
      return {
        metadata,
        appliedParams: [],
        unknownParams: [],
        warnings: [],
        error: `A form section can be laid out in 1, 2 or 4 columns — got ${invalid.join(", ")}.`
      };
    }
  }

  // The form's default (first) section, then every declared titled section. Declaring a
  // section is what fixes its ORDER on the screen and its layout; a section a field
  // references without being declared is created afterwards with the default layout.
  if (overrides.form && next.sections.length > 0) applySectionLayout(next.sections[0], overrides.form);
  for (const sectionOverride of sectionOverrides) {
    const existingIndex: number = findSectionIndexByTitle(next, sectionOverride.title);
    if (existingIndex === -1) {
      const created: IFormSection = createSection(sectionOverride.title);
      applySectionLayout(created, sectionOverride);
      next.sections.push(created);
      continue;
    }
    applySectionLayout(next.sections[existingIndex], sectionOverride);
  }

  const index: Map<string, FieldLocation> = indexFieldsByParam(next);
  const appliedParams: string[] = [];
  const unknownParams: string[] = [];
  const warnings: string[] = [];
  // Keyed by the FIELD OBJECT, not by an index: a `section:` re-homes fields between
  // sections, which invalidates every (sectionIndex, itemIndex) pair captured earlier.
  const requestedOrder: Map<ICRUDFormField, number> = new Map<ICRUDFormField, number>();
  const requestedSection: Map<ICRUDFormField, string> = new Map<ICRUDFormField, string>();

  // Resolve every requested WIDGET before mutating anything. The backend does not
  // validate `elementType` at all (`ValidateMetadataOutput` only counts sections), so
  // a bogus or type-incompatible widget would ship a broken input silently — refuse
  // the whole set instead, with the valid list, and let the author fix and re-run.
  const resolvedElements: Map<string, string> = new Map<string, string>();
  for (const override of overrides.fields) {
    const requested: string = override.element?.trim() ?? "";
    if (requested.length === 0) continue;
    const location: FieldLocation | undefined = index.get(foldParamNameForMatch(override.param));
    if (!location) continue; // surfaces as an unknown param in the apply loop below
    const field: ICRUDFormField = next.sections[location.sectionIndex].items[location.itemIndex];
    const canonical: string | undefined = resolveElementTypeName(requested);
    const valid: string = describeElementTypeOptions(field.typeName);
    if (!canonical) {
      return {
        metadata,
        appliedParams: [],
        unknownParams: [],
        warnings: [],
        error:
          `"${requested}" is not a form input widget. Valid widgets for field "${override.param}" ` +
          `(type ${field.typeName}): ${valid}.`
      };
    }
    if (!isElementTypeValidForFieldType(canonical, field.typeName)) {
      return {
        metadata,
        appliedParams: [],
        unknownParams: [],
        warnings: [],
        error:
          `Widget "${canonical}" is not available for field "${override.param}", which is of type ` +
          `${field.typeName}. Valid widgets for that type: ${valid}.`
      };
    }
    resolvedElements.set(foldParamNameForMatch(override.param), canonical);
  }

  for (const override of overrides.fields) {
    const location: FieldLocation | undefined = index.get(foldParamNameForMatch(override.param));
    if (!location) {
      unknownParams.push(override.param);
      continue;
    }
    const field: ICRUDFormField = next.sections[location.sectionIndex].items[location.itemIndex];

    if (override.hidden !== undefined) field.isEnabled = !override.hidden;
    if (override.isReadonly !== undefined) field.isReadonlyField = override.isReadonly;

    // Widget first — the required rules below depend on the FINAL element type.
    const resolvedElement: string | undefined = resolvedElements.get(foldParamNameForMatch(override.param));
    if (resolvedElement) field.elementType = resolvedElement;

    if (override.scale !== undefined && Number.isFinite(override.scale)) {
      if (DECIMAL_SCALE_TYPES.includes(field.typeName)) {
        field.decimalScale = override.scale;
      } else {
        warnings.push(
          `Field "${override.param}" is of type ${field.typeName}, which has no decimal places — \`scale\` was ignored.`
        );
      }
    }

    if (override.isRequired === true) {
      if (field.isReadonlyField) {
        warnings.push(
          `Field "${override.param}" is readonly, so its \`required\` was dropped — a read-only field cannot be filled in.`
        );
        field.isRequired = false;
      } else if (REQUIRED_INCOMPATIBLE_ELEMENTS.includes(field.elementType)) {
        warnings.push(
          `Field "${override.param}" renders as ${field.elementType}, which is always set — \`required\` was dropped.`
        );
        field.isRequired = false;
      } else {
        field.isRequired = true;
      }
    } else if (override.isRequired === false) {
      field.isRequired = false;
    } else if (field.isReadonlyField) {
      // Readonly always wins over a required flag the default metadata carried.
      field.isRequired = false;
    }

    const label: string = override.label?.trim() ?? "";
    if (label.length > 0) field.name = label;

    if (override.order !== undefined && Number.isFinite(override.order)) {
      requestedOrder.set(field, override.order);
    }

    const sectionTitle: string = override.section?.trim() ?? "";
    if (sectionTitle.length > 0) requestedSection.set(field, sectionTitle);

    appliedParams.push(override.param);
  }

  // Re-home the fields that named a section. A title no section carries yet is created
  // here with the default layout — forgiving on purpose: an author who writes
  // `section:"Karar"` without declaring it should get the section, not an error.
  for (const [field, title] of requestedSection) {
    let targetIndex: number = findSectionIndexByTitle(next, title);
    if (targetIndex === -1) {
      next.sections.push(createSection(title));
      targetIndex = next.sections.length - 1;
    }
    const target: IFormSection = next.sections[targetIndex];
    if (target.items.includes(field)) continue;
    for (const section of next.sections) {
      const at: number = section.items.indexOf(field);
      if (at !== -1) section.items.splice(at, 1);
    }
    target.items.push(field);
  }

  // Ordering runs AFTER re-homing, so a field sorts inside the section it ended up in.
  for (const section of next.sections) {
    const hasRequestedOrder: boolean = section.items.some((item: ICRUDFormField): boolean => requestedOrder.has(item));
    if (!hasRequestedOrder) continue;
    const decorated: { item: ICRUDFormField; order: number; originalIndex: number }[] = section.items.map(
      (item: ICRUDFormField, itemIndex: number) => ({
        item,
        order: requestedOrder.get(item) ?? Number.POSITIVE_INFINITY,
        originalIndex: itemIndex
      })
    );
    decorated.sort((a, b): number => (a.order === b.order ? a.originalIndex - b.originalIndex : a.order - b.order));
    section.items = decorated.map((d): ICRUDFormField => d.item);
  }

  // A section with no fields renders NOTHING (`FormBuilderService` skips it), so an
  // emptied-out default section is dropped rather than left as dead metadata. The last
  // section always stays — `ValidateMetadataOutput` rejects a sectionless form.
  if (next.sections.length > 1) {
    const nonEmpty: IFormSection[] = next.sections.filter((section: IFormSection): boolean => section.items.length > 0);
    if (nonEmpty.length > 0) next.sections = nonEmpty;
  }

  const enabledBefore: number = countEnabledFields(metadata);
  const enabledAfter: number = countEnabledFields(next);
  if (enabledBefore > 0 && enabledAfter === 0) {
    return {
      metadata,
      appliedParams: [],
      unknownParams,
      warnings,
      error:
        "This field configuration would hide EVERY field, leaving a blank task screen. " +
        "Keep at least one field visible (an approver still has to see what they are deciding on)."
    };
  }

  return { metadata: next, appliedParams, unknownParams, warnings };
}

/**
 * The DEFAULT shape of a user task's managed screen: the data the assignee REVIEWS
 * is read-only, and only what the task itself WRITES stays editable.
 *
 * A task's writable data is exactly its mapped OUTPUT parameters (`MAP_OUTPUT`) —
 * a comment, a decision value the assignee types. Everything else on the form is
 * the request as it arrived, and letting an approver silently edit it is a defect,
 * not a feature.
 *
 * Deterministic on purpose: it holds even when the model authors no SCREEN_FIELD at
 * all, which is the on-prem (Qwen-class) floor this harness must work on. Explicit
 * SCREEN_FIELD config is layered ON TOP via `mergeWorkflowFormOverrides`, so the
 * author can always opt a field back to editable.
 */
export function deriveTaskFormReadonlyDefault(
  boParamNames: readonly string[],
  editableParamNames: readonly string[]
): WorkflowFormOverrides {
  const editable: Set<string> = new Set<string>(
    editableParamNames.map((name: string): string => foldParamNameForMatch(name))
  );
  const fields: WorkflowFormFieldOverride[] = [];
  for (const name of boParamNames) {
    const folded: string = foldParamNameForMatch(name);
    if (folded.length === 0 || editable.has(folded)) continue;
    fields.push({ param: name, isReadonly: true });
  }
  return { fields };
}

/**
 * Layer explicit (DSL-authored) field config over the deterministic default.
 * Per-KNOB, not per-field: a `SCREEN_FIELD … label:"…"` must not discard the
 * default's `readonly` for that same field. Params are matched culture-invariantly,
 * so two spellings of one param collapse into a single entry; the base's spelling
 * is kept because it comes from the real workflow parameter.
 */
export function mergeWorkflowFormOverrides(
  base: WorkflowFormOverrides,
  explicit: WorkflowFormOverrides
): WorkflowFormOverrides {
  const merged: Map<string, WorkflowFormFieldOverride> = new Map<string, WorkflowFormFieldOverride>();
  for (const field of base.fields) {
    merged.set(foldParamNameForMatch(field.param), field);
  }
  for (const field of explicit.fields) {
    const key: string = foldParamNameForMatch(field.param);
    const existing: WorkflowFormFieldOverride | undefined = merged.get(key);
    if (!existing) {
      merged.set(key, field);
      continue;
    }
    merged.set(key, { ...existing, ...field, param: existing.param });
  }
  // Sections and the default-section layout are authored ONLY explicitly (the
  // deterministic default has no opinion on them), so they pass straight through —
  // dropping them here would silently discard every SCREEN_SECTION / SCREEN_FORM.
  const sections: readonly WorkflowFormSectionOverride[] | undefined = explicit.sections ?? base.sections;
  const form: WorkflowFormLayoutOverride | undefined = explicit.form ?? base.form;
  return {
    fields: Array.from(merged.values()),
    ...(sections && sections.length > 0 ? { sections } : {}),
    ...(form ? { form } : {})
  };
}
